import React, { useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Loader2,
  Plus,
  Search,
  Pencil,
  Trash2,
  ChevronRight,
  Tags,
  GripVertical,
  MoreVertical,
} from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { toast } from "@/hooks/use-toast";
import { useCategories, Category, HierarchicalCategory } from "@/hooks/use-categories";
import { CategoryCreateModal } from "@/components/shared/CategoryCreateModal";
import { CategoryEditModal } from "@/components/categories/CategoryEditModal";
import * as LucideIcons from "lucide-react";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

const Categories = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingCategory, setEditingCategory] = useState<Category | null>(null);
  const [deletingCategory, setDeletingCategory] = useState<Category | null>(null);
  const [parentForCreate, setParentForCreate] = useState<Category | null>(null);

  const { categories, hierarchicalCategories, loading: categoriesLoading, refetch } = useCategories('all');

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleDelete = async () => {
    if (!deletingCategory) return;

    try {
      const { error } = await supabase
        .from('categories')
        .delete()
        .eq('id', deletingCategory.id);

      if (error) throw error;

      toast({
        title: 'Categoria excluída',
        description: `${deletingCategory.name} foi removida com sucesso.`
      });

      refetch();
    } catch (error: any) {
      toast({
        variant: 'destructive',
        title: 'Erro ao excluir',
        description: error.message
      });
    } finally {
      setDeletingCategory(null);
    }
  };

  const handleCreateSubcategory = (parent: Category) => {
    setParentForCreate(parent);
    setShowCreateModal(true);
  };

  const handleDragEnd = async (event: DragEndEvent, categoriesList: HierarchicalCategory[]) => {
    const { active, over } = event;
    
    if (!over || active.id === over.id) return;

    // Check if the dragged item is a child category
    const allChildren = categoriesList.flatMap(c => c.children);
    const activeChild = allChildren.find(c => c.id === active.id);
    const overChild = allChildren.find(c => c.id === over.id);

    if (activeChild && overChild && activeChild.parent_id === overChild.parent_id) {
      // Both are children of the same parent — reorder children
      const parent = categoriesList.find(c => c.id === activeChild.parent_id);
      if (!parent) return;
      const sortedChildren = [...parent.children].sort((a, b) =>
        ((a as any).sort_order || 0) - ((b as any).sort_order || 0)
      );
      const oldIdx = sortedChildren.findIndex(c => c.id === active.id);
      const newIdx = sortedChildren.findIndex(c => c.id === over.id);
      if (oldIdx === -1 || newIdx === -1) return;
      const reordered = arrayMove(sortedChildren, oldIdx, newIdx);
      try {
        for (let i = 0; i < reordered.length; i++) {
          await supabase.from('categories').update({ sort_order: i }).eq('id', reordered[i].id);
        }
        toast({ title: 'Ordem atualizada', description: 'A ordem das subcategorias foi salva.' });
        refetch();
      } catch (error: any) {
        toast({ variant: 'destructive', title: 'Erro ao reordenar', description: error.message });
      }
      return;
    }

    // Parent category reorder
    const oldIndex = categoriesList.findIndex(c => c.id === active.id);
    const newIndex = categoriesList.findIndex(c => c.id === over.id);

    if (oldIndex === -1 || newIndex === -1) return;

    const reorderedCategories = arrayMove(categoriesList, oldIndex, newIndex);
    
    try {
      const updates = reorderedCategories.map((cat, index) => ({
        id: cat.id,
        sort_order: index,
      }));

      for (const update of updates) {
        await supabase
          .from('categories')
          .update({ sort_order: update.sort_order })
          .eq('id', update.id);
      }

      toast({
        title: 'Ordem atualizada',
        description: 'A ordem das categorias foi salva.'
      });

      refetch();
    } catch (error: any) {
      toast({
        variant: 'destructive',
        title: 'Erro ao reordenar',
        description: error.message
      });
    }
  };

  const filteredCategories = hierarchicalCategories.filter(cat => {
    const matchesSearch = cat.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      cat.children.some(child => child.name.toLowerCase().includes(searchTerm.toLowerCase()));
    return matchesSearch;
  });

  // Sort by sort_order
  const sortByOrder = (cats: HierarchicalCategory[]) => 
    [...cats].sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0));

  const incomeCategories = sortByOrder(filteredCategories.filter(c => c.type === 'income'));
  const expenseCategories = sortByOrder(filteredCategories.filter(c => c.type === 'expense'));

  const renderIcon = (iconName: string, color: string) => {
    const IconComponent = (LucideIcons as any)[iconName];
    if (!IconComponent) return <Tags className="h-4 w-4" style={{ color }} />;
    return <IconComponent className="h-4 w-4" style={{ color }} />;
  };

  const SortableRow = ({ category, isChild = false }: { category: Category & { sort_order?: number }; isChild?: boolean }) => {
    const {
      attributes,
      listeners,
      setNodeRef,
      transform,
      transition,
      isDragging,
    } = useSortable({ id: category.id });

    const style = {
      transform: CSS.Transform.toString(transform),
      transition,
      opacity: isDragging ? 0.5 : 1,
    };

    return (
      <TableRow 
        ref={setNodeRef} 
        style={style} 
        className={`${isChild ? "bg-muted/30" : ""} ${isDragging ? "bg-primary/10" : ""}`}
      >
        <TableCell className={isChild ? "pl-10" : ""}>
          <div className="flex items-center gap-2">
            {!category.is_system && (
              <button
                className="cursor-grab active:cursor-grabbing touch-none text-muted-foreground hover:text-foreground"
                {...attributes}
                {...listeners}
              >
                <GripVertical className="h-4 w-4" />
              </button>
            )}
            {isChild && <ChevronRight className="h-3 w-3 text-muted-foreground" />}
            <div
              className="h-6 w-6 rounded-full flex items-center justify-center"
              style={{ backgroundColor: `${category.color}20` }}
            >
              {renderIcon(category.icon, category.color)}
            </div>
            <span className="font-medium">{category.name}</span>
            {category.is_system && (
              <Badge variant="secondary" className="text-xs">Sistema</Badge>
            )}
          </div>
        </TableCell>
        <TableCell>
          <Badge variant={category.type === 'income' ? 'default' : 'destructive'}>
            {category.type === 'income' ? 'Receita' : 'Despesa'}
          </Badge>
        </TableCell>
        <TableCell>
          <div className="h-4 w-4 rounded-full border" style={{ backgroundColor: category.color }} />
        </TableCell>
        <TableCell className="text-right">
          {!category.is_system && (
            <div className="flex items-center justify-end gap-2">
              {!isChild && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handleCreateSubcategory(category)}
                  title="Adicionar subcategoria"
                >
                  <Plus className="h-4 w-4" />
                </Button>
              )}
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setEditingCategory(category)}
              >
                <Pencil className="h-4 w-4" />
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setDeletingCategory(category)}
                className="text-destructive hover:text-destructive"
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
          )}
        </TableCell>
      </TableRow>
    );
  };

  const MobileCategoryCard = ({ category, isChild = false }: { category: Category & { sort_order?: number; children?: Category[] }; isChild?: boolean }) => {
    const parentCategory = isChild
      ? categories.find(c => c.id === category.parent_id)
      : null;

    return (
      <div className={`flex items-center gap-3 p-3 border rounded-lg min-h-[48px] ${isChild ? "ml-4 bg-muted/30" : ""}`}>
        <div
          className="h-6 w-6 rounded-full flex items-center justify-center shrink-0"
          style={{ backgroundColor: `${category.color}20` }}
        >
          {renderIcon(category.icon, category.color)}
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-medium text-sm truncate">{category.name}</div>
          {isChild && parentCategory && (
            <div className="text-xs text-muted-foreground">Sub de: {parentCategory.name}</div>
          )}
          {category.is_system && (
            <Badge variant="secondary" className="text-xs mt-0.5">Sistema</Badge>
          )}
        </div>
        <Badge variant={category.type === 'income' ? 'default' : 'destructive'} className="shrink-0 text-xs">
          {category.type === 'income' ? 'Receita' : 'Despesa'}
        </Badge>
        {!category.is_system && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon" className="h-10 w-10 shrink-0">
                <MoreVertical className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              {!isChild && (
                <DropdownMenuItem onClick={() => handleCreateSubcategory(category)}>
                  <Plus className="h-4 w-4 mr-2" />
                  Subcategoria
                </DropdownMenuItem>
              )}
              <DropdownMenuItem onClick={() => setEditingCategory(category)}>
                <Pencil className="h-4 w-4 mr-2" />
                Editar
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={() => setDeletingCategory(category)}
                className="text-destructive"
              >
                <Trash2 className="h-4 w-4 mr-2" />
                Excluir
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </div>
    );
  };

  const CategorySection = ({ title, categories, type }: { title: string; categories: HierarchicalCategory[]; type: 'income' | 'expense' }) => {
    const isDraggingDisabled = searchTerm.length > 0;

    return (
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="text-lg flex items-center gap-2">
            <Badge variant={type === 'income' ? 'default' : 'destructive'}>
              {categories.length}
            </Badge>
            {title}
          </CardTitle>
          {!isDraggingDisabled && categories.length > 0 && (
            <span className="hidden md:inline text-xs text-muted-foreground">
              Arraste para reordenar
            </span>
          )}
        </CardHeader>
        <CardContent>
          {categories.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <Tags className="h-8 w-8 mx-auto mb-2 opacity-50" />
              <p>Nenhuma categoria encontrada</p>
            </div>
          ) : (
            <>
              {/* Mobile card list */}
              <div className="md:hidden space-y-2">
                {categories.map((cat) => {
                  const sortedChildren = [...cat.children].sort((a, b) =>
                    ((a as any).sort_order || 0) - ((b as any).sort_order || 0)
                  );
                  return (
                    <React.Fragment key={cat.id}>
                      <MobileCategoryCard category={cat} />
                      {sortedChildren.map((child) => (
                        <MobileCategoryCard key={child.id} category={child} isChild />
                      ))}
                    </React.Fragment>
                  );
                })}
              </div>

              {/* Desktop table with drag-to-reorder */}
              <div className="hidden md:block">
                <DndContext
                  sensors={sensors}
                  collisionDetection={closestCenter}
                  onDragEnd={(e) => handleDragEnd(e, categories)}
                >
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Nome</TableHead>
                        <TableHead>Tipo</TableHead>
                        <TableHead>Cor</TableHead>
                        <TableHead className="text-right">Ações</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      <SortableContext
                        items={categories.flatMap(c => [c.id, ...c.children.map(ch => ch.id)])}
                        strategy={verticalListSortingStrategy}
                        disabled={isDraggingDisabled}
                      >
                        {categories.map((cat) => {
                          const sortedChildren = [...cat.children].sort((a, b) =>
                            ((a as any).sort_order || 0) - ((b as any).sort_order || 0)
                          );
                          return (
                            <React.Fragment key={cat.id}>
                              <SortableRow category={cat} />
                              {sortedChildren.map((child) => (
                                <SortableRow key={child.id} category={child} isChild />
                              ))}
                            </React.Fragment>
                          );
                        })}
                      </SortableContext>
                    </TableBody>
                  </Table>
                </DndContext>
              </div>
            </>
          )}
        </CardContent>
      </Card>
    );
  };

  if (categoriesLoading) {
    return (
      <div className="flex-1 flex items-center justify-center p-8">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6 space-y-4 sm:space-y-6">
      {/* Page Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Tags className="h-6 w-6" />
            Gerenciar Categorias
          </h1>
          <p className="text-muted-foreground">
            Organize suas categorias e subcategorias de receitas e despesas
          </p>
        </div>
        <Button size="sm" onClick={() => {
          setParentForCreate(null);
          setShowCreateModal(true);
        }}>
          <Plus className="h-4 w-4 sm:mr-2" />
          <span className="hidden sm:inline">Nova Categoria</span>
        </Button>
      </div>

      {/* Search */}
      <div className="relative max-w-full sm:max-w-md">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          placeholder="Buscar categorias..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="pl-10"
        />
      </div>

      {/* Categories Tables */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <CategorySection
          title="Categorias de Despesa"
          categories={expenseCategories}
          type="expense"
        />
        <CategorySection
          title="Categorias de Receita"
          categories={incomeCategories}
          type="income"
        />
      </div>

      {/* Create Modal */}
      <CategoryCreateModal
        open={showCreateModal}
        onOpenChange={(open) => {
          setShowCreateModal(open);
          if (!open) setParentForCreate(null);
        }}
        onSuccess={refetch}
        defaultParentId={parentForCreate?.id}
        defaultType={parentForCreate?.type}
      />

      {/* Edit Modal */}
      <CategoryEditModal
        category={editingCategory}
        open={!!editingCategory}
        onOpenChange={(open) => !open && setEditingCategory(null)}
        onSuccess={refetch}
      />

      {/* Delete Confirmation */}
      <AlertDialog open={!!deletingCategory} onOpenChange={() => setDeletingCategory(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Excluir categoria?</AlertDialogTitle>
            <AlertDialogDescription>
              Tem certeza que deseja excluir "{deletingCategory?.name}"?
              Esta ação não pode ser desfeita e pode afetar transações existentes.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Excluir
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default Categories;
